import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  RotateCcw,
  BookOpen,
  Lightbulb,
  TrendingUp,
  Award,
  Clock
} from 'lucide-react';

interface CoachingFeedback {
  id: string;
  timestamp_seconds: number;
  feedback_type: 'positive' | 'suggestion' | 'research_insight' | 'course_recommendation';
  title: string;
  content: string;
  research_citations: string[];
  confidence_score: number;
}

interface CoachingVideoPlayerProps {
  videoId: string;
  autoAnalyze?: boolean;
}

export const CoachingVideoPlayer: React.FC<CoachingVideoPlayerProps> = ({
  videoId,
  autoAnalyze = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [video, setVideo] = useState<any>(null);
  const [feedback, setFeedback] = useState<CoachingFeedback[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<CoachingFeedback | null>(null);

  useEffect(() => {
    loadVideo();
    loadFeedback();
  }, [videoId]);

  useEffect(() => {
    if (autoAnalyze && video && video.status === 'processing' && !isAnalyzing) {
      analyzeVideo();
    }
  }, [video, autoAnalyze]);

  const loadVideo = async () => {
    const { data } = await supabase
      .from('practice_videos')
      .select(`
        *,
        courses (title)
      `)
      .eq('id', videoId)
      .single();
    
    if (data) setVideo(data);
  };

  const loadFeedback = async () => {
    const { data } = await supabase
      .from('coaching_feedback')
      .select('*')
      .eq('practice_video_id', videoId)
      .order('timestamp_seconds');
    
    if (data) setFeedback(data as CoachingFeedback[]);
  };

  const analyzeVideo = async () => {
    setIsAnalyzing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('analyze-practice-video', {
        body: { videoId }
      });

      if (error) throw error;
      
      await loadVideo();
      await loadFeedback();
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const seekTo = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      setCurrentTime(seconds);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFeedbackIcon = (type: string) => {
    switch (type) {
      case 'positive': return <Award className="h-4 w-4 text-green-600" />;
      case 'suggestion': return <Lightbulb className="h-4 w-4 text-blue-600" />;
      case 'research_insight': return <BookOpen className="h-4 w-4 text-purple-600" />;
      case 'course_recommendation': return <TrendingUp className="h-4 w-4 text-orange-600" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getFeedbackColor = (type: string) => {
    switch (type) {
      case 'positive': return 'bg-green-100 text-green-800 border-green-200';
      case 'suggestion': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'research_insight': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'course_recommendation': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getActiveFeedback = () => {
    return feedback.filter(f => 
      Math.abs(f.timestamp_seconds - currentTime) <= 5
    );
  };

  if (!video) {
    return <div>Loading video...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Video Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div>
              <h2>{video.title}</h2>
              {video.courses && (
                <p className="text-sm text-muted-foreground mt-1">
                  Related to: {video.courses.title}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {video.status === 'processing' && (
                <Button
                  onClick={analyzeVideo}
                  disabled={isAnalyzing}
                  size="sm"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Video'}
                </Button>
              )}
              <Badge variant="outline">
                {feedback.length} Insights
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Player */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-0">
              <div className="relative bg-black rounded-t-lg overflow-hidden">
                <video
                  ref={videoRef}
                  src={video.video_url}
                  className="w-full aspect-video"
                  onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
                
                {/* Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={togglePlay}
                      className="text-white hover:bg-white/20"
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleMute}
                      className="text-white hover:bg-white/20"
                    >
                      {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </Button>
                    
                    <div className="flex-1 flex items-center gap-2 text-white text-sm">
                      <span>{formatTime(currentTime)}</span>
                      <div className="flex-1 bg-white/20 rounded-full h-1 relative">
                        <div 
                          className="bg-white rounded-full h-1 transition-all"
                          style={{ width: `${(currentTime / duration) * 100}%` }}
                        />
                        {/* Feedback markers */}
                        {feedback.map(f => (
                          <div
                            key={f.id}
                            className="absolute top-0 w-1 h-3 -mt-1 bg-yellow-400 rounded cursor-pointer"
                            style={{ left: `${(f.timestamp_seconds / duration) * 100}%` }}
                            onClick={() => seekTo(f.timestamp_seconds)}
                            title={f.title}
                          />
                        ))}
                      </div>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Feedback */}
          {getActiveFeedback().length > 0 && (
            <Card className="mt-4 border-primary/50 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-primary" />
                  Active Coaching Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {getActiveFeedback().map(f => (
                  <div key={f.id} className="p-3 rounded-lg border bg-card">
                    <div className="flex items-start gap-3">
                      {getFeedbackIcon(f.feedback_type)}
                      <div className="flex-1">
                        <h4 className="font-medium">{f.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {f.content}
                        </p>
                        {f.research_citations.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">
                              Based on: {f.research_citations.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Feedback Timeline */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Coaching Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-3">
                  {feedback.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">
                        {video.status === 'processing' 
                          ? 'Analyzing video for feedback...' 
                          : 'No feedback available yet'
                        }
                      </p>
                    </div>
                  ) : (
                    feedback.map((f, index) => (
                      <div key={f.id}>
                        <div 
                          className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                            selectedFeedback?.id === f.id ? 'ring-2 ring-primary' : ''
                          } ${getFeedbackColor(f.feedback_type)}`}
                          onClick={() => {
                            seekTo(f.timestamp_seconds);
                            setSelectedFeedback(f);
                          }}
                        >
                          <div className="flex items-start gap-2">
                            {getFeedbackIcon(f.feedback_type)}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">
                                  {formatTime(f.timestamp_seconds)}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {Math.round(f.confidence_score * 100)}%
                                </Badge>
                              </div>
                              <h4 className="font-medium text-sm mt-1">{f.title}</h4>
                              <p className="text-xs mt-1 line-clamp-2">
                                {f.content}
                              </p>
                            </div>
                          </div>
                        </div>
                        {index < feedback.length - 1 && <Separator className="my-2" />}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Description */}
      {video.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{video.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};